import { Delete, DragIndicator } from '@mui/icons-material'
import { Box, Button, Card, IconButton, Tooltip, Typography } from '@mui/material'
import { AttributeCollectionValue, AttributeCollectionValueItem } from 'graphql/types'
import { DragDropContext, Draggable, DraggableProvided, Droppable, OnDragEndResponder } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { getOrderedAttributeValueUnion } from 'utils/dataType/functions'
import {
    AttributeValueUnion,
    createInitialAttributeValues,
    getCollectionValueItemFromAttributeValues,
} from 'utils/dataType/types'
import { v4 } from 'uuid'
import { AttributeValueEdit } from './AttributeValueEdit'
import { AttributeValueEditProps } from './DataItemDetailsPanel'

// FIXME: this tanks performance especially for text inputs. On fast PCs its alright, but really far from great
// FIXME: there are sometimes problems with copy by id/reference so that editing one object edits two. Unknown source, couldnt reproduce

// This is getting a bit ugly due to nesting
export const AttributeCollectionValueEdit = (props: AttributeValueEditProps) => {
    const { attribute, attributeValue, onUpdateValue, disabled, selectedVersion } = props

    const { t } = useTranslation()

    if (attributeValue.__typename !== 'AttributeCollectionValue') return null
    if (!attribute || attribute.__typename !== 'AttributeCollection') return null

    const nestedAttributes = selectedVersion.snapshotAttributesCollection.find(
        (item) => item.id === attribute.dataType,
    )?.attributes

    if (!nestedAttributes)
        return <Typography>{t('drawerPanels.dataItemDetailsPanel.attributeCollectionValueEdit.noSubType')}</Typography>

    const { value } = attributeValue

    const override = (values: Partial<AttributeCollectionValue>) =>
        onUpdateValue({
            ...attributeValue,
            ...values,
        })

    const onDragEnd: OnDragEndResponder = (result) => {
        const { destination, source } = result
        if (!destination) return
        if (destination.droppableId === source.droppableId && destination.index === source.index) return
        if (destination.droppableId !== source.droppableId) return

        const list = Array.from(value)
        const [removed] = list.splice(source.index, 1)
        list.splice(destination.index, 0, removed)

        override({ value: list })
    }

    const onClickAddOption = () => {
        const item = getCollectionValueItemFromAttributeValues(v4(), createInitialAttributeValues(nestedAttributes))
        override({ value: [...value, { ...item }] })
    }

    const onClickDeleteOption = (attributeValue: AttributeCollectionValueItem) => {
        override({ value: value.filter((item) => item.id !== attributeValue.id) })
    }

    const renderAttributeValue = (item: AttributeCollectionValueItem, index: number) => {
        const nestedValues = getOrderedAttributeValueUnion(nestedAttributes, item)

        const setNestedAttributeValue = (attributeValue: AttributeValueUnion) => {
            const newNestedValues = nestedValues.map((nestedValue) =>
                nestedValue.attributeID === attributeValue.attributeID ? attributeValue : nestedValue,
            )
            const newItem = getCollectionValueItemFromAttributeValues(item.id, newNestedValues)

            override({ value: value.map((nestedValue) => (nestedValue.id === newItem.id ? newItem : nestedValue)) })
        }

        return (
            <Draggable key={item.id} draggableId={item.id} index={index} isDragDisabled={disabled}>
                {(provided: DraggableProvided) => (
                    <Card
                        key={item.id}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        component={Box}
                        display={'flex'}
                        flexDirection={'row'}
                        padding={'8px'}
                        marginBottom={'8px'}
                        alignItems={'flex-start'}
                        position={'relative'}
                        sx={{
                            ':hover': {
                                '> button': {
                                    display: 'flex',
                                },
                            },
                            '> button': {
                                display: 'none',
                            },
                            paddingY: '16px',
                            paddingRight: '16px',
                        }}
                    >
                        <DragIndicator fontSize="large" color={disabled ? 'secondary' : 'action'} />
                        <Box display="flex" flexDirection={'column'} flex={1} gap={'8px'} marginLeft={'8px'}>
                            {nestedValues.map((nestedAttributeValue) => (
                                <AttributeValueEdit
                                    key={nestedAttributeValue.attributeID}
                                    {...props}
                                    attribute={nestedAttributes.find(
                                        (attribute) => attribute.id === nestedAttributeValue.attributeID,
                                    )}
                                    attributeValue={nestedAttributeValue}
                                    onUpdateValue={setNestedAttributeValue}
                                    hideDivider
                                    index={index}
                                    disabled={disabled}
                                />
                            ))}
                        </Box>
                        <Tooltip title={t('drawerPanels.dataItemDetailsPanel.attributeCollectionValueEdit.remove')}>
                            <span>
                                <IconButton
                                    sx={{ position: 'absolute', bottom: 6, left: 6 }}
                                    onClick={() => onClickDeleteOption(item)}
                                    disabled={disabled}
                                    color="error"
                                >
                                    <Delete />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Card>
                )}
            </Draggable>
        )
    }

    return (
        <>
            <DragDropContext onDragEnd={onDragEnd}>
                <Box sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
                    <Droppable droppableId={'options'}>
                        {(provided) => (
                            <span {...provided.droppableProps} ref={provided.innerRef}>
                                {attributeValue.value.map(renderAttributeValue)}
                                {provided.placeholder}
                            </span>
                        )}
                    </Droppable>
                    {/* <FormHelperText error sx={{ marginBottom: 1 }}>
                        {errors.find((error) => error.type === `innerCollectionEmpty${attribute.id}`)?.message ?? ''}
                    </FormHelperText> */}

                    <Button variant={'contained'} fullWidth onClick={onClickAddOption} disabled={disabled}>
                        {t('drawerPanels.dataItemDetailsPanel.attributeCollectionValueEdit.add')}
                    </Button>
                </Box>
            </DragDropContext>
        </>
    )
}
